import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import { Firm } from "../../types/firm";
import { useFirmStore } from "../../store/firmStore";
import { PrimaryButton, TextButton } from "../../components/ButtonComponents";
import { useConfirmModal } from "../../hooks/useConfirmModal";

interface FirmClearDataDialogProps {
  isOpen: boolean;
  onClose: () => void;
  firm: Firm | null;
  onSuccess: () => void;
}

export const FirmClearDataDialog: React.FC<FirmClearDataDialogProps> = ({
  isOpen,
  onClose,
  firm,
  onSuccess
}) => {
  const [clearTransactions, setClearTransactions] = useState(false);
  const [clearStocks, setClearStocks] = useState(false);
  const [clearAccounts, setClearAccounts] = useState(false);
  const [loading, setLoading] = useState(false);
  const { confirm, modal: confirmModal } = useConfirmModal();

  if (!firm) return null;

  const handleClear = async () => {
    if (!clearTransactions && !clearStocks && !clearAccounts) {
      toast.warning("Lütfen temizlemek istediğiniz veri tiplerini seçin.");
      return;
    }

    const isConfirmed = await confirm(
      `${firm.name} firması için seçtiğiniz veriler KALICI olarak silinecektir. Bu işlem geri alınamaz. Kesinlikle emin misiniz?`
    );

    if (isConfirmed) {
      setLoading(true);
      try {
        const store = (useFirmStore as any).getState();
        if (store.clearFirmData) {
          const result = await store.clearFirmData(firm.id, {
            clearTransactions,
            clearStocks,
            clearAccounts
          });
          if (result.success) {
            toast.success(result.message || "Firma verileri başarıyla temizlendi.");
            onSuccess();
            onClose();
          } else {
            toast.error(result.message || "Veriler temizlenirken hata oluştu.");
          }
        }
      } catch (error: any) {
        toast.error("Temizlik işlemi sırasında beklenmeyen bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <TextButton onClick={onClose} disabled={loading}>
        İptal
      </TextButton>
      <PrimaryButton 
        onClick={handleClear} 
        disabled={loading || (!clearTransactions && !clearStocks && !clearAccounts)}
        className="bg-red-600 hover:bg-red-700 text-white"
      >
        {loading ? "Temizleniyor..." : "Seçili Verileri Temizle"}
      </PrimaryButton>
    </div>
  );

  return (
    <>
      {confirmModal}
      <Dialog
        header={`Veri Temizleme: ${firm.name}`}
        visible={isOpen}
        style={{ width: "450px" }}
        onHide={onClose}
        footer={footer}
      >
        <div className="flex flex-col gap-4 py-4">
          <p className="text-sm text-gray-600 mb-2">
            Aşağıdan silmek istediğiniz veri türlerini seçiniz. İşlem peyderpey yapılacaktır (timeout'a düşmemesi için sadece seçtikleriniz silinir).
          </p>

          <label className="flex items-center gap-3 p-3 border rounded-md hover:bg-gray-50 cursor-pointer">
            <input 
              type="checkbox" 
              className="w-5 h-5 text-red-600"
              checked={clearTransactions} 
              onChange={(e) => setClearTransactions(e.target.checked)} 
              disabled={loading}
            />
            <div className="flex flex-col">
              <span className="font-semibold text-gray-800">Hareket Verileri</span>
              <span className="text-xs text-gray-500">Sipariş, ziyaret, depo görevleri, kasa ve banka hareketleri, indirim geçmişi vb.</span>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 border rounded-md hover:bg-gray-50 cursor-pointer">
            <input 
              type="checkbox" 
              className="w-5 h-5 text-red-600"
              checked={clearAccounts} 
              onChange={(e) => setClearAccounts(e.target.checked)} 
              disabled={loading}
            />
            <div className="flex flex-col">
              <span className="font-semibold text-gray-800">Cari Kartları</span>
              <span className="text-xs text-gray-500">Müşteriler, cari hesaplar ve onlara bağlı adres bilgileri.</span>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 border rounded-md hover:bg-gray-50 cursor-pointer">
            <input 
              type="checkbox" 
              className="w-5 h-5 text-red-600"
              checked={clearStocks} 
              onChange={(e) => setClearStocks(e.target.checked)} 
              disabled={loading}
            />
            <div className="flex flex-col">
              <span className="font-semibold text-gray-800">Stok Kartları</span>
              <span className="text-xs text-gray-500">Ürünler, fiyatlar, varyantlar, lokasyonlar, kategoriler, markalar vb.</span>
            </div>
          </label>
        </div>
      </Dialog>
    </>
  );
};
